import React, { useState, useRef, useEffect } from 'react';
import { useTeam } from '@/contexts/TeamContext';
import { useNavigate } from 'react-router-dom';
import { CaretDown, User, UsersThree, Plus, Check } from '@phosphor-icons/react';

function WorkspaceSwitcher() {
  const { teams, activeTeam, switchTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const label = activeTeam ? activeTeam.team_name : 'Personal';
  const Icon = activeTeam ? UsersThree : User;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors text-slate-700"
        data-testid="workspace-switcher">
        <Icon size={14} weight="duotone" className={activeTeam ? 'text-navy' : 'text-slate-400'} />
        <span className="max-w-[120px] truncate">{label}</span>
        <CaretDown size={12} className="text-slate-400" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 shadow-lg rounded-lg overflow-hidden z-50" data-testid="workspace-menu">
          {/* Personal */}
          <button onClick={() => { switchTeam(null); setOpen(false); navigate('/dashboard'); }}
            className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
            data-testid="switch-personal">
            <User size={14} weight="duotone" className="text-slate-400" />
            <span className="text-sm text-slate-700 flex-1">Personal</span>
            {!activeTeam && <Check size={14} weight="bold" className="text-navy" />}
          </button>

          {teams.length > 0 && <div className="border-t border-slate-100" />}

          {teams.map(team => (
            <button key={team.team_id}
              onClick={() => { switchTeam(team); setOpen(false); navigate('/dashboard'); }}
              className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
              data-testid={`switch-team-${team.team_id}`}>
              <UsersThree size={14} weight="duotone" className="text-navy" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 truncate">{team.team_name}</p>
                <p className="text-[10px] text-slate-400">{team.my_role} · {team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
              </div>
              {activeTeam?.team_id === team.team_id && <Check size={14} weight="bold" className="text-navy" />}
            </button>
          ))}

          <div className="border-t border-slate-100" />
          <button onClick={() => { setOpen(false); navigate('/team/create'); }}
            className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center gap-2.5 text-navy transition-colors"
            data-testid="create-team-btn">
            <Plus size={14} weight="bold" />
            <span className="text-sm font-medium">Create Team</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default WorkspaceSwitcher;
